/**
 * Signal implementation for reactive state management
 */

import {
  currentEffect,
  defaultEquals,
  STALE,
  SignalNode,
  getExecCount
} from './constants';

import {
  enqueueEffect,
  scheduleFlush
} from './queue';

import { debugSignal } from './debug';

// Import global state access
const GLOBAL_STATE_KEY = '__JELENJS_REACTIVE_STATE__';

function getCurrentEffectFromGlobal() {
  if (typeof globalThis !== 'undefined' && (globalThis as any)[GLOBAL_STATE_KEY]) {
    return (globalThis as any)[GLOBAL_STATE_KEY].currentEffect;
  }
  if (typeof window !== 'undefined' && (window as any)[GLOBAL_STATE_KEY]) {
    return (window as any)[GLOBAL_STATE_KEY].currentEffect;
  }
  return currentEffect; // fallback
}

// Types for signals
export type Setter<T> = (value: T | ((prev: T) => T)) => T;
export type Getter<T> = () => T;
export type Signal<T> = [get: Getter<T>, set: Setter<T>];
export type ReadonlySignal<T> = Getter<T>;

// Debug flag is now imported from debug.ts

// Global signal registry to ensure all modules share the same signal tracking
const SIGNAL_REGISTRY_KEY = '__JELENJS_SIGNAL_REGISTRY__';

function getGlobalSignalRegistry(): Set<Function> {
  if (typeof globalThis !== 'undefined') {
    if (!(globalThis as any)[SIGNAL_REGISTRY_KEY]) {
      (globalThis as any)[SIGNAL_REGISTRY_KEY] = new Set<Function>();
    }
    return (globalThis as any)[SIGNAL_REGISTRY_KEY];
  }
  
  if (typeof window !== 'undefined') {
    if (!(window as any)[SIGNAL_REGISTRY_KEY]) {
      (window as any)[SIGNAL_REGISTRY_KEY] = new Set<Function>();
    }
    return (window as any)[SIGNAL_REGISTRY_KEY];
  }
  
  // Fallback
  return new Set<Function>();
}

const globalSignalRegistry = getGlobalSignalRegistry();

// Symbol to mark signal getter functions (more reliable than WeakSet)
const SIGNAL_GETTER_SYMBOL = Symbol('signalGetter');

/**
 * Check if a function is a signal getter
 */
export function isSignalGetter(fn: any): boolean {
  if (typeof fn !== 'function') return false;
  
  // Check Symbol first (most reliable)
  if (fn[SIGNAL_GETTER_SYMBOL] === true) {
    return true;
  }
  
  // Check global registry as fallback
  return globalSignalRegistry.has(fn);
}

/**
 * Create a reactive signal with optimized getter/setter
 *
 * @param initialValue The initial value of the signal
 * @param equals Optional equality function to determine when to trigger updates
 * @returns A tuple of [getter, setter] functions
 */
export function createSignal<T>(initialValue: T, equals = defaultEquals): Signal<T> {
  // Generate a unique ID for this signal for debugging
  const signalId = Math.random().toString(36).substr(2, 5);
  
  debugSignal(signalId, 'Created with initial value:', initialValue);
  
  // Simple structure to store signal state (SolidJS-inspired)
  const node: SignalNode = {
    value: initialValue,         // Current value 
    subscribers: new Set(),      // Set of dependent effects (legacy)
    observers: null,             // Array of observers (SolidJS-style)
    observerSlots: null          // Slots for O(1) removal
  };

  // Getter function - tracks dependencies during effect execution
  function getter(): T {
    const globalCurrentEffect = getCurrentEffectFromGlobal();
    debugSignal(signalId, 'Getter called, currentEffect:', globalCurrentEffect);
    
    // If we're inside an effect, add it as a subscriber
    if (globalCurrentEffect !== null) {
      debugSignal(signalId, `Adding effect #${globalCurrentEffect.id} as subscriber`);
      
      // Add current effect to this signal's subscribers (legacy Set-based)
      node.subscribers.add(globalCurrentEffect);

      // Add this signal to effect's sources (new array-based)
      const sourceIndex = globalCurrentEffect.sources.length;
      globalCurrentEffect.sources.push(node);
      globalCurrentEffect.sourceSlots.push(node.subscribers.size - 1);
      
      debugSignal(signalId, `Tracked by effect #${globalCurrentEffect.id} at index ${sourceIndex}`);
    } else {
      debugSignal(signalId, 'No current effect to track');
    }
    
    // Return the current value
    return node.value;
  }

  // Mark the getter function as a signal getter for proper detection
  getter[SIGNAL_GETTER_SYMBOL] = true;
  globalSignalRegistry.add(getter);
  
  debugSignal(signalId, 'Marked getter with Symbol:', getter[SIGNAL_GETTER_SYMBOL]);
  debugSignal(signalId, 'Added to global registry, size:', globalSignalRegistry.size);

  // Setter function - updates value and notifies subscribers
  function setter(newValue: T | ((prev: T) => T)): T {
    // Handle updater functions
    if (typeof newValue === 'function') {
      // Cast to correct type to handle updater function
      newValue = (newValue as (prev: T) => T)(node.value);
    }
    
    // Only update if value changed (using provided equality function)
    if (!equals(node.value, newValue as T)) {
      debugSignal(signalId, 'Value changing from', node.value, 'to', newValue);
      
      // Update the value
      node.value = newValue as T;
      
      // Skip notification if no subscribers
      if (node.subscribers.size === 0) {
        debugSignal(signalId, 'No subscribers to notify');
        return node.value;
      }
      
      debugSignal(signalId, `Notifying ${node.subscribers.size} subscribers`);
      
      // Create a copy of subscribers to avoid issues if the set changes during iteration
      const subscribers = Array.from(node.subscribers);
      
      // Notify all subscribers by marking them stale and enqueueing
      for (const effect of subscribers) {
        if (effect && !effect.disposed) {
          // Mark as stale
          effect.state |= STALE;
          
          // Add to the queue for execution
          enqueueEffect(effect);
          
          debugSignal(signalId, `Marked effect #${effect.id} as stale`);
        }
      }
      
      // Schedule a flush to run all pending effects
      scheduleFlush();
    } else {
      debugSignal(signalId, 'Value unchanged, skipping update');
    }
    
    return node.value;
  }

  // Return the getter/setter tuple
  return [getter, setter];
}